import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

export interface StepItem {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  status?: 'wait' | 'process' | 'finish' | 'error';
}

/** Ant Design `Steps` replacement — horizontal or vertical progress steps. */
export function Steps({
  current = 0, items, direction = 'horizontal', size, className, onChange,
}: {
  current?: number;
  items: StepItem[];
  direction?: 'horizontal' | 'vertical';
  size?: 'default' | 'small';
  className?: string;
  onChange?: (current: number) => void;
}) {
  const statusOf = (i: number, s?: StepItem['status']) => s ?? (i < current ? 'finish' : i === current ? 'process' : 'wait');
  const dot = size === 'small' ? 'size-7 text-[13px]' : 'size-9 text-sm';

  return (
    <ol className={cn('flex', direction === 'vertical' ? 'flex-col gap-0' : 'items-start', className)}>
      {items.map((it, i) => {
        const st = statusOf(i, it.status);
        const done = st === 'finish';
        const active = st === 'process';
        const error = st === 'error';
        const circle = (
          <span className={cn('grid shrink-0 place-items-center rounded-full font-semibold ring-1 ring-inset', dot,
            error ? 'bg-danger-500 text-white ring-danger-500'
              : done ? 'bg-brand-600 text-white ring-brand-600'
                : active ? 'bg-brand-50 text-brand-700 ring-brand-400 dark:bg-brand-900/30 dark:text-brand-300'
                  : 'bg-surface-2 text-subtle ring-line')}>
            {it.icon ?? (done ? <Check className="size-4" /> : i + 1)}
          </span>
        );
        const label = (
          <div className="min-w-0">
            <div className={cn('text-sm font-semibold', active || done ? 'text-content' : 'text-muted')}>{it.title}</div>
            {it.description != null && <div className="mt-0.5 text-xs text-subtle">{it.description}</div>}
          </div>
        );
        if (direction === 'vertical') {
          return (
            <li key={i} className="flex gap-3 pb-6 last:pb-0">
              <div className="flex flex-col items-center">
                {circle}
                {i < items.length - 1 && <span className={cn('mt-1 w-px flex-1', done ? 'bg-brand-500' : 'bg-line')} />}
              </div>
              <button type="button" disabled={!onChange} onClick={() => onChange?.(i)} className="pt-1 text-left disabled:cursor-default">{label}</button>
            </li>
          );
        }
        return (
          <li key={i} className="flex flex-1 items-center">
            <button type="button" disabled={!onChange} onClick={() => onChange?.(i)} className="flex items-center gap-2.5 text-left disabled:cursor-default">
              {circle}{label}
            </button>
            {i < items.length - 1 && <span className={cn('mx-3 h-px flex-1', done ? 'bg-brand-500' : 'bg-line')} />}
          </li>
        );
      })}
    </ol>
  );
}
